import { DEFAULT_OG_IMAGE, PERSON_NAME, PROFILE_IMAGE, SAME_AS, SITE_URL } from "../site";

const AUTHOR_ID = `${SITE_URL}/#person`;
const WEBSITE_ID = `${SITE_URL}/#website`;

/** A fully qualified URL for a site-relative path; absolute URLs pass through. */
export function absUrl(path) {
	if (!path) return SITE_URL;
	if (path.startsWith("http")) return path;
	return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

/** The canonical Person entity - same @id everywhere on the site. */
export const personEntity = {
	"@type": "Person",
	"@id": AUTHOR_ID,
	name: PERSON_NAME,
	url: SITE_URL,
	image: PROFILE_IMAGE,
	jobTitle: ["Co-Founder", "CTO", "Machine Learning Engineer"],
	worksFor: { "@type": "Organization", name: "2am", url: "https://2am-music.com" },
	knowsAbout: [
		"Audio AI",
		"LLMs",
		"Machine Learning",
		"Real-time Inference",
		"ML Infrastructure",
	],
	sameAs: SAME_AS,
};

/** The canonical WebSite entity. */
export const websiteEntity = {
	"@type": "WebSite",
	"@id": WEBSITE_ID,
	url: SITE_URL,
	name: PERSON_NAME,
	description:
		"Co-Founder & CTO at 2am. Technical articles on audio AI, LLMs, and production ML systems.",
	publisher: { "@id": AUTHOR_ID },
	inLanguage: "en",
};

/**
 * @typedef {object} ArticleFields
 * @property {string} [type] "TechArticle" (default) or "Article"
 * @property {string} url canonical URL
 * @property {string} [title]
 * @property {string} [description]
 * @property {string} [datePublished]
 * @property {string} [dateModified]
 * @property {string} [image] absolute URL
 * @property {string} [lang] the entry's language, "en" when empty
 * @property {string[]} [keywords]
 * @property {string} [section]
 * @property {string | null} [readingTime]
 * @property {number} [wordCount]
 */

/**
 * The Article/TechArticle entity for one page.
 * @param {ArticleFields} fields
 */
export function buildArticleEntity({
	type = "TechArticle",
	url,
	title,
	description,
	datePublished,
	dateModified,
	image,
	lang,
	keywords = [],
	section,
	readingTime,
	wordCount,
}) {
	return {
		"@type": type,
		"@id": `${url}#article`,
		mainEntityOfPage: { "@type": "WebPage", "@id": url },
		headline: title,
		description,
		image: { "@type": "ImageObject", url: image || DEFAULT_OG_IMAGE, width: 1200, height: 630 },
		datePublished,
		dateModified: dateModified || datePublished,
		author: { "@id": AUTHOR_ID },
		publisher: { "@id": AUTHOR_ID },
		inLanguage: lang || "en",
		isPartOf: { "@id": WEBSITE_ID },
		...(keywords.length && { keywords: keywords.join(", ") }),
		...(section && { articleSection: section }),
		...(readingTime && { timeRequired: readingTime }),
		...(wordCount && { wordCount }),
	};
}

/** A BreadcrumbList from [{name, url?}], or null when there are no crumbs. */
export function buildBreadcrumbList(breadcrumbs = []) {
	if (!breadcrumbs.length) return null;
	return {
		"@type": "BreadcrumbList",
		itemListElement: breadcrumbs.map((crumb, i) => ({
			"@type": "ListItem",
			position: i + 1,
			name: crumb.name,
			...(crumb.url && { item: crumb.url }),
		})),
	};
}

/**
 * The JSON-LD @graph for an article page: article, breadcrumbs (if any),
 * person and website.
 * @param {ArticleFields & { breadcrumbs?: { name: string, url?: string }[] }} fields
 */
export function buildArticleJsonLd({ breadcrumbs = [], ...article }) {
	const breadcrumbList = buildBreadcrumbList(breadcrumbs);
	const graph = [
		buildArticleEntity(article),
		...(breadcrumbList ? [breadcrumbList] : []),
		personEntity,
		websiteEntity,
	];
	return { "@context": "https://schema.org", "@graph": graph };
}

/** The site-wide Organization entity. */
export function buildOrganizationSchema() {
	return {
		"@type": "Organization",
		"@id": `${SITE_URL}/#organization`,
		name: PERSON_NAME,
		url: SITE_URL,
		logo: { "@type": "ImageObject", url: PROFILE_IMAGE },
		description: `Technical articles on audio AI, LLMs, and production ML systems by ${PERSON_NAME}.`,
		founder: { "@id": AUTHOR_ID },
		sameAs: SAME_AS,
		knowsAbout: ["Audio AI", "LLMs", "Machine Learning", "Real-time Inference", "Startup CTO"],
	};
}
